import { FC, useEffect } from 'react';
import { Box, Paper } from '@mui/material';
import { usePaginatedData, useSortingParams } from '../../../_core/hooks';
import { FamilyDetails } from '../../components/family-details/family-details.component';
import { EngagementsTable } from '../../../engagement/components/engagements-table';
import {
	useArchiveEngagementMutation,
	useCreateEngagementMutation,
	useDeleteEngagementMutation,
	useGenerateEngagementsCustomFieldFileUrlMutation,
	useGetEngagementsByFamilyIdQuery,
	useGetEngagementStatusesQuery,
	useSendRemindersLinguistLinkEngagementsMutation,
	useUpdateEngagementMutation,
} from '../../../../store/api/engagements.api';
import { EngagementDto } from '@families-link/shared';
import { setLocalStorageEngagementId, setLocalStorageFamilyId } from '../../../_core/utils';

import { useTranslation } from 'react-i18next';
import { useGetAllFieldsQuery } from '../../../../store/api/customRequirements.api';
import {
	useGetAllFamiliesByDistrictIdQuery,
	useGetChildrenByDistrictIdQuery,
	useGetChildrenByFamilyIdsQuery,
	useGetFamilyQuery,
} from '../../../../store/api/families.api';
import { useGetAllProvidersByDistrictIdQuery, useGetAllProvidersQuery } from '../../../../store/api/providers.api';
import { useOpenModalForm } from '../../../engagement/hooks/modal-forms/use-open-modal-form.hook';
import { useOpenConfirmModal } from '../../../engagement/hooks/modal-forms/use-open-confirm-modal-form.hook';
import { CoreButton } from '../../../_core/components/_ui/core-button';
import AddIcon from '@mui/icons-material/Add';
import { ConfirmationDeleteIcon, DeleteIcon } from '../../../_core/components/icons';
import { DistrictRoutes } from '../../../district/constants';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { useSessionStorage } from '../../../../hooks';
import { NotFoundComponent, ForbiddenComponent } from '../../../_core/components/exceptions';
import { ArchiveRedIcon } from '../../../_core/constants/icons.constants';
import { StudentsListComponent } from '../../components/family-details/students-list.component';
import { boxHeaderStyles, buttonHeaderStyles } from '../../../_core/styles';
import { mergeSx } from 'merge-sx';
import { EngagementColumnName } from '../../../engagement/hooks/table-columns/engagements-table-columns.hook';
import { AllowedEngagementActions } from '../../../engagement/components/engagements-table/engagements-table-actions.component';
import { useGetDistrictQuery } from '../../../../store/api/district.api';
import { useOpenRemindersConfirmModal } from '../../../engagement/hooks/modal-forms/use-open-reminders-modal-form.hook';

const engagementColumnsList: EngagementColumnName[] = [
	'title',
	'customId',
	'provider',
	'language',
	'family',
	'school',
	'program',
	'duration',
	'status',
	'surveyStatus',
	'actions',
];
const allowedEngagementActions: AllowedEngagementActions[] = ['view', 'archive', 'delete', 'edit', 'email', 'sms'];

export const FamilyDetailsPage: FC = () => {
	const { familyId } = useParams();
	setLocalStorageFamilyId(familyId ?? '');
	const { data: currentFamily, isError, error } = useGetFamilyQuery({ id: familyId }, { skip: !familyId });
	const { data: currentChildrenList } = useGetChildrenByFamilyIdsQuery(
		{ families: currentFamily?._id },
		{ skip: !currentFamily?._id }
	);
	const [sessionStorageDistrictId, setSessionStorageDistrictId] = useSessionStorage('districtId');
	const { data: currentDistrict } = useGetDistrictQuery(
		{ id: currentFamily?.districtId },
		{ skip: !currentFamily?.districtId }
	);

	const isLinguistLink = !!currentDistrict?.districtLinguistLinkId;

	useEffect(() => {
		if (currentFamily?.districtId) {
			setSessionStorageDistrictId(currentFamily?.districtId);
		}
	}, [currentFamily?.districtId, setSessionStorageDistrictId]);

	const navigate = useNavigate();

	const [t] = useTranslation();
	const { data: fieldsListFromDistrict } = useGetAllFieldsQuery(
		{ districtId: currentFamily?.districtId },
		{ skip: !currentFamily?.districtId }
	);
	const { data: providersList } = useGetAllProvidersByDistrictIdQuery(
		{ districtId: currentFamily?.districtId },
		{ skip: !currentFamily?.districtId }
	);
	const { data: familiesList } = useGetAllFamiliesByDistrictIdQuery(
		{ district: currentFamily?.districtId },
		{ skip: !currentFamily?.districtId }
	);
	const { data: childrenList } = useGetChildrenByDistrictIdQuery(
		{ districtId: currentFamily?.districtId },
		{ skip: !currentFamily?.districtId }
	);
	const { data: statusesList } = useGetEngagementStatusesQuery();

	const { sortingArray, sortingParams, handleSortingChange } = useSortingParams();
	const { requestObject, meta, page, handleResetPage, itemsList, fetchMore } = usePaginatedData<EngagementDto>(
		useGetEngagementsByFamilyIdQuery,
		{ familyId, ...sortingParams },
		{ skip: !familyId, refetchOnMountOrArgChange: true }
	);
	const { refetch, isFetching } = requestObject;

	useEffect(() => {
		handleResetPage();
	}, [sortingArray, handleResetPage]);

	const handlePagination = () => {
		handleResetPage();

		if (page === 1) {
			refetch();
		}
	};

	const [generateEngagementsCustomFieldFileUrl] = useGenerateEngagementsCustomFieldFileUrlMutation();

	const [createEngagement] = useCreateEngagementMutation();
	const featchActionCreate = async (data: any) => {
		await createEngagement(data).unwrap();
	};
	const handleOpenModalCreate = useOpenModalForm({
		featchAction: featchActionCreate,
		handlePagination,
		isCreate: true,
		districtId: currentFamily?.districtId,
		submitButtonName: t('engagements.forms.buttons.create'),
		toastText: 'Engagement record was successfully created',
		fieldsListFromDistrict,
		providersList,
		familiesList: currentFamily ? [currentFamily] : [],
		childrenList: currentChildrenList ?? [],
		defaultValues: { family: currentFamily },
		statusesList,
		generateFileUrlFunction: generateEngagementsCustomFieldFileUrl,
	});

	const [updateEngagement] = useUpdateEngagementMutation();
	const featchActionEdit = async (data: any) => {
		await updateEngagement({ id: data?._id?.toString(), data }).unwrap();
	};
	const handleOpenModalEdit = useOpenModalForm({
		featchAction: featchActionEdit,
		handlePagination,
		isCreate: false,
		districtId: currentFamily?.districtId,
		submitButtonName: t('engagements.forms.buttons.save'),
		toastText: 'Engagement record was successfully updated',
		fieldsListFromDistrict,
		providersList,
		familiesList,
		childrenList,
		statusesList,
		generateFileUrlFunction: generateEngagementsCustomFieldFileUrl,
	});

	const [deleteEngagement] = useDeleteEngagementMutation();
	const featchActionDelete = async (data: EngagementDto) => {
		await deleteEngagement({ id: data._id }).unwrap();
	};
	const handleOpenDeleteConfirmModal = useOpenConfirmModal({
		featchAction: featchActionDelete,
		handlePagination,
		title: t('engagements.forms.delete-title'),
		description: t('engagements.forms.delete-description'),
		icon: <ConfirmationDeleteIcon sx={{ fontSize: '40px', color: (theme) => theme.colors.red }} />,
		submitButtonName: t('engagements.forms.buttons.delete'),
		toastText: 'Engagement record was successfully deleted',
		danger: true,
	});

	const handleOnView = (e: React.MouseEvent<HTMLElement>, engagement: EngagementDto) => {
		if (!engagement) return;

		const params: Record<string, string> = {
			engagementId: engagement._id,
		};

		const targetPath = isLinguistLink
			? generatePath(DistrictRoutes.Engagements.DetailsLL, {
					...params,
					engagementItemId: engagement.projectItemId,
			  })
			: generatePath(DistrictRoutes.Engagements.Details, params);

		navigate(targetPath, {
			state: { isLinguistLink: isLinguistLink },
		});
	};

	const [archiveEngagement] = useArchiveEngagementMutation();

	const fetchActionArchive = async (data: EngagementDto) => {
		await archiveEngagement({ id: data._id }).unwrap();
	};
	const handleArchiveConfirmModal = useOpenConfirmModal({
		featchAction: fetchActionArchive,
		handlePagination,
		title: t('engagements.forms.archive-title'),
		description: t('engagements.forms.archive-description'),
		icon: <ArchiveRedIcon />,
		danger: true,
		submitButtonName: t('engagements.forms.buttons.archive'),
		toastText: 'Engagement record was successfully archived.',
	});

	const [sendRemindersLinguistLinkEngagements] = useSendRemindersLinguistLinkEngagementsMutation();

	const fetchActionSendReminder = async (engagement: EngagementDto) => {
		const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
		const response = await sendRemindersLinguistLinkEngagements({
			engagementIds: [engagement._id],
			timeZone,
		}).unwrap();

		return response;
	};

	const handleSendReminder = useOpenRemindersConfirmModal({
		featchAction: fetchActionSendReminder,
		handlePagination,
		title: t('engagements.forms.send-reminder'),
		description: t('engagements.forms.send-reminder-description-one'),
		//icon: <ConfirmationDeleteIcon sx={{ fontSize: '40px', color: (theme) => theme.colors.red }} />,
		submitButtonName: t('engagements.forms.buttons.send'),
		toastText: 'Engagement reminder were successfully sent',
		danger: false,
	});

	if (isError) {
		switch ((error as any).data.statusCode) {
			case 404:
			case 400:
				return <NotFoundComponent componentName="family" />;
			case 403:
				return <ForbiddenComponent componentName="family" />;
			default:
				break;
		}
	}

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
			<Box sx={{ display: 'flex', width: '100%', height: '32%', gap: 1 }}>
				<Paper elevation={0} sx={{ width: '55%', p: 3 }}>
					<FamilyDetails familyId={familyId} />
				</Paper>
				<Paper elevation={0} sx={{ width: '45%', p: 3 }}>
					<StudentsListComponent familyId={familyId} />
				</Paper>
			</Box>
			<Paper
				elevation={0}
				sx={{
					flex: 1,
					p: 3,
					display: 'flex',
					height: '70%',
					mt: 1,
					borderBottomRightRadius: 0,
					borderBottomLeftRadius: 0,
					flexDirection: 'column',
				}}
			>
				{isLinguistLink ? null : (
					<Box
						sx={mergeSx(boxHeaderStyles, {
							justifyContent: 'flex-end',
						})}
					>
						<CoreButton
							onClick={() => {
								handleOpenModalCreate();
							}}
							sx={buttonHeaderStyles}
						>
							<AddIcon />
							{t('engagements.add-engagements')}
						</CoreButton>
					</Box>
				)}
				{isLinguistLink ? (
					<EngagementsTable
						engagementColumnsList={engagementColumnsList}
						allowedEngagementActions={allowedEngagementActions}
						data={itemsList}
						loading={isFetching}
						hasMore={meta?.hasNextPage}
						onDelete={handleOpenDeleteConfirmModal}
						onEdit={handleOpenModalEdit}
						onView={handleOnView}
						onArchive={handleArchiveConfirmModal}
						sorting={sortingArray}
						onSortingChange={handleSortingChange}
						fetchMore={fetchMore}
						isLinguistLink={isLinguistLink}
						onSendReminder={handleSendReminder}
					/>
				) : (
					<EngagementsTable
						engagementColumnsList={engagementColumnsList}
						allowedEngagementActions={allowedEngagementActions}
						data={itemsList}
						loading={isFetching}
						hasMore={meta?.hasNextPage}
						onDelete={handleOpenDeleteConfirmModal}
						onEdit={handleOpenModalEdit}
						onView={handleOnView}
						onArchive={handleArchiveConfirmModal}
						sorting={sortingArray}
						onSortingChange={handleSortingChange}
						fetchMore={fetchMore}
					/>
				)}
			</Paper>
		</Box>
	);
};
